import { Button, Card, Col, Divider, Flex, Form, FormProps, Input, Row, Skeleton, Typography } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// services
import { checkPhoneValidation } from 'utils/validators/phone';
import { isRTL } from './../../../../core/services/language';
import { onCreateUser, onGetAllPermissions } from '../services/usersService';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import NotificationService from 'core/services/notification';

// components
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';
import FormElement from 'components/formElement/formElement';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import SwitchElement from 'components/switch/switch';
import PageTitle from 'components/title/title';
import FormPassword from 'components/formPassword/formPassword';

// models
import { Permission } from '../models/permission';

const UsersManagementCreatePage: FunctionComponent = () => {
	const { t } = useTranslation();
	const [userForm] = Form.useForm();
	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [permissions, setPermissions] = useState<Permission[]>([]);

	useEffect(() => {
		getPermissionsHandler();
	}, []);

	type fieldType = {
		name: string;
		email: string;
		national_id: string;
		phone_country_code: string;
		phone_number: string;
		password: string;
		password_confirmation: string;
		permissions: string[];
	};

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		userForm.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	const validatePhone = (_: any, value: string) => {
		if (checkPhoneValidation(userForm.getFieldValue('phone_country_code'), value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const validatePassword = (_: any, value: string) => {
		if (!value || userForm.getFieldValue('password') === value) {
			return Promise.resolve();
		}
		return Promise.reject(new Error(t('COMMON.PASSWORDS_NOT_MATCH')));
	};

	const onSwitchChange = (fieldName: string, value: boolean) => {
		const permissions = userForm.getFieldValue('permissions') || [];

		if (value) {
			permissions.push(fieldName);
		} else {
			const index = permissions.indexOf(fieldName);
			if (index > -1) {
				permissions.splice(index, 1);
			}
		}

		userForm.setFieldValue('permissions', permissions);
	};

	const getPermissionsHandler = async () => {
		try {
			setIsLoading(true);
			const permissions = await onGetAllPermissions();
			setPermissions(permissions);
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
		}
	};

	const onFinish: FormProps<fieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);
			await onCreateUser(values);
			NotificationService.success(t('COMMON.SUCCESS'), t('USERS_MANAGEMENT.USER_CREATED_SUCCESSFULLY'));
			setIsSubmitting(false);
			navigate('/dashboard/users-management');
		} catch (error) {
			setIsSubmitting(false);
			mapResponseErrors(error, userForm);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.USERS_MANAGEMENT')} />

			<BackButton />

			<Card>
				<ComponentHeader title={t('USERS_MANAGEMENT.ADD_NEW_USER')} subtitle={t('USERS_MANAGEMENT.FILL_DETAILS_TO_ADD_USER')} />

				<Form form={userForm} onFinish={onFinish}>
					<Row gutter={[16, 16]}>
						<Col span={24} md={12}>
							<FormElement label={t('COMMON.NAME')} name="name" type="text" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]} />
						</Col>

						<Col span={24} md={12}>
							<FormElement
								label={t('COMMON.NATIONAL_ID')}
								name="national_id"
								type="number"
								rules={[
									{ required: true, message: t('COMMON.FIELD_REQUIRED') },
									{ len: 10, message: t('COMMON.NATIONAL_ID_LENGTH') },
								]}
							/>
						</Col>

						<Col span={24} md={12}>
							<FormElement
								label={t('COMMON.EMAIL')}
								name="email"
								type="email"
								rules={[
									{ required: true, message: t('COMMON.FIELD_REQUIRED') },
									{ type: 'email', message: t('COMMON.EMAIL_INVALID') },
								]}
							/>
						</Col>

						<Col span={24} md={12}>
							<Form.Item name="phone_number" rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
								<PhoneInputComponent fullWidth onChange={onChangePhone} />
							</Form.Item>
							<Form.Item name="phone_country_code" hidden>
								<Input />
							</Form.Item>
						</Col>

						<Col span={24} md={12}>
							<FormPassword
								name="password"
								label={t('COMMON.PASSWORD')}
								rules={[
									{ required: true, message: t('COMMON.PASSWORD_REQUIRED') },
									{ min: 8, message: t('COMMON.PASSWORD_MIN_8') },
								]}
							/>
						</Col>

						<Col span={24} md={12}>
							<FormPassword
								name="password_confirmation"
								label={t('COMMON.PASSWORD_CONFIRMATION')}
								rules={[{ required: true, message: t('COMMON.PASSWORD_CONFIRMATION_REQUIRED') }, { validator: validatePassword }]}
							/>
						</Col>
					</Row>

					<Divider />

					<ComponentHeader title={t('USERS_MANAGEMENT.ACCESS_ROLE')} subtitle={t('USERS_MANAGEMENT.SELECT_ACCESS_AUTHORITY')} />

					<Row gutter={[16, 16]}>
						{isLoading ? (
							[1, 2, 3, 4, 5, 6].map((item) => (
								<Col span={24} md={12} lg={8} key={item}>
									<Skeleton.Button active block style={{ minHeight: 22 }} />
								</Col>
							))
						) : (
							<>
								{permissions.map((permission) => (
									<Col span={24} md={12} lg={8} key={permission.name}>
										<SwitchElement
											label={isRTL() ? permission.translated_display_name.ar : permission.translated_display_name.en}
											onChange={(value) => onSwitchChange(permission.name, value)}
										/>
									</Col>
								))}

								<Col span={24}>
									<Form.Item
										style={{ position: 'absolute' }}
										name="permissions"
										rules={[{ required: true, message: t('USERS_MANAGEMENT.SELECT_AR_LEAST_ONE') }]}>
										<Input hidden />
									</Form.Item>
								</Col>
							</>
						)}

						<Col span={24}>
							<Flex justify="flex-end">
								<Button type="primary" className="mt-4 px-3 py-2" htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
									{t('COMMON.CREATE')}
								</Button>
							</Flex>
						</Col>
					</Row>
				</Form>
			</Card>
		</>
	);
};

export default UsersManagementCreatePage;
